import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { ActionMetadataOutDTO, EntityTypeEnum, FactMetadataOutDTO, OperatorMetadataOutDTO, RuleMetadataOutDTO, TriggerMetadataOutDTO } from '@api/ringil-api.v2';
import { OperatorKey } from '../ruleAst';

/**
 * The rule engine's vocabulary, fetched once.
 *
 * Everything the editor offers — triggers, facts, operators, actions, enum values, reserved words
 * — comes from `GET /ai-dispatcher/metadata`. Nothing in this feature hard-codes a fact, an
 * operator or an action, so the backend can add to the catalogue without a frontend release.
 */
export interface RuleMetadataContextValue {
  metadata?: RuleMetadataOutDTO;
  loading: boolean;
  failed: boolean;
  reload: () => void;

  triggerByKey: (key: string) => TriggerMetadataOutDTO | undefined;
  factByKey: (key: string) => FactMetadataOutDTO | undefined;
  actionByKey: (key: string) => ActionMetadataOutDTO | undefined;
  operatorByKey: (key: string) => OperatorMetadataOutDTO | undefined;

  /** Facts the given trigger can actually provide. */
  factsForTrigger: (triggerKey: string) => FactMetadataOutDTO[];
  /** Operators legal for a fact's type, in metadata order. */
  operatorsForFact: (factKey: string) => OperatorMetadataOutDTO[];
  /** Actions runnable against the entity the given trigger fires on. */
  actionsForTrigger: (triggerKey: string) => ActionMetadataOutDTO[];
  /** Whether an operator takes a value at all — false for IS_NULL / IS_NOT_NULL. */
  operatorTakesValue: (operator: OperatorKey) => boolean;

  reservedWords: string[];
}

const RuleMetadataContext = createContext<RuleMetadataContextValue>(undefined);

const byKey = <T extends { key: string }>(items: T[] | undefined): Map<string, T> => new Map((items ?? []).map(item => [item.key, item]));

export const RuleMetadataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { metadata: loadMetadata } = useContext(ApiContext);
  // ApiContext rebuilds its value every render, so keying `reload` on the function itself would
  // make the effect below refetch the vocabulary on every render instead of once.
  const loadMetadataRef = useRef(loadMetadata);
  loadMetadataRef.current = loadMetadata;
  const [metadata, setMetadata] = useState<RuleMetadataOutDTO>(undefined);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setFailed(false);
    loadMetadataRef.current(
      data => {
        setMetadata(data);
        setLoading(false);
      },
      null,
      {
        onError: (_error, showStandardError) => {
          showStandardError();
          setFailed(true);
          setLoading(false);
        },
      },
    );
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo<RuleMetadataContextValue>(() => {
    const triggers = byKey(metadata?.triggers);
    const facts = byKey(metadata?.facts);
    const actions = byKey(metadata?.actions);
    const operators = byKey(metadata?.operators);

    const factsForTrigger = (triggerKey: string) =>
      (metadata?.facts ?? []).filter(fact => {
        // An empty list means "every trigger" — see `Facts.kt`. Treating it as "no trigger" would
        // hide the entire expedition catalogue, since only the offer facts are restricted.
        if (!fact.availableForTriggers || fact.availableForTriggers.length === 0) return true;
        return fact.availableForTriggers.includes(triggerKey);
      });

    return {
      metadata,
      loading,
      failed,
      reload,

      triggerByKey: key => triggers.get(key),
      factByKey: key => facts.get(key),
      actionByKey: key => actions.get(key),
      operatorByKey: key => operators.get(key),

      factsForTrigger,

      operatorsForFact: factKey => {
        const fact = facts.get(factKey);
        if (!fact) return [];
        return fact.operators.map(operator => operators.get(operator)).filter(operator => operator !== undefined);
      },

      actionsForTrigger: triggerKey => {
        const entityType = triggers.get(triggerKey)?.entityType;
        if (!entityType) return metadata?.actions ?? [];
        return (metadata?.actions ?? []).filter(action => (action.entityTypes ?? []).some(type => String(type) === String(entityType as EntityTypeEnum)));
      },

      operatorTakesValue: operator => operators.get(operator)?.takesValue ?? true,

      reservedWords: metadata?.reservedWords ?? [],
    };
  }, [metadata, loading, failed, reload]);

  return <RuleMetadataContext.Provider value={value}>{children}</RuleMetadataContext.Provider>;
};

export const useRuleMetadata = (): RuleMetadataContextValue => {
  const context = useContext(RuleMetadataContext);
  if (!context) throw new Error('useRuleMetadata must be used inside a RuleMetadataProvider');
  return context;
};
